import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import Navigation from '../components/Navigation.js';

const DetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  
  // 状态管理
  const [record, setRecord] = useState({
    id: id,
    audioId: id, // 添加 audioId 字段，确保录音完成后能正确显示
    content: '',
    timestamp: new Date().toISOString(),
    isFavorite: false,
    transcriptionStatus: 'idle', // idle, processing, completed, error
    polishStatus: 'idle',
    transcriptionText: '',
    polishedText: '',
    instructionText: '',
    hasAudioFile: false, // 音频文件存在标识，默认为false
    durationSeconds: 0 // 数据库中的音频时长
  });

  // 异步操作状态
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [audioData, setAudioData] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // 统一的提示状态
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // 用于跟踪原始内容，比较是否有变更
  const polishedTextRef = useRef('');
  const transcriptionTextRef = useRef('');
  const instructionRef = useRef('');

  // 用于避免重复检查音频文件
  const audioFileCheckedRef = useRef(false);

  // 用于避免重复初始化页面
  const pageInitializedRef = useRef(false);

  // textarea refs for auto-resize
  const polishedTextareaRef = useRef(null);
  const transcriptionTextareaRef = useRef(null);
  const instructionTextareaRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0); // 动态音频时长
  const audioRef = useRef(null);

  // 初始化页面
  const initializePage = useCallback(async () => {
    try {
      console.log('🔄 initializePage 被调用，audioId:', id, '当前初始化状态:', pageInitializedRef.current);

      // 避免重复初始化（但允许在不同音频ID时重新初始化）
      if (pageInitializedRef.current && pageInitializedRef.current === id) {
        console.log('📋 页面已初始化，跳过重复初始化，audioId:', id);
        return;
      }

      // 添加一个额外的锁来防止React严格模式的双重调用
      if (pageInitializedRef.current === 'initializing') {
        console.log('📋 页面正在初始化中，跳过重复调用，audioId:', id);
        return;
      }

      // 设置初始化中标志
      pageInitializedRef.current = 'initializing';

      // 1. 检查是否有待处理的音频数据
      const pendingAudio = sessionStorage.getItem('pendingAudio');

      if (pendingAudio) {
        const audioInfo = JSON.parse(pendingAudio);
        console.log('📋 发现待处理音频:', audioInfo);

        // 验证audioId是否匹配当前页面
        if (audioInfo.audioId === id) {
          setAudioData(audioInfo);
          setDuration(audioInfo.duration || 0);

          // 设置音频URL用于播放
          if (audioInfo.audioUrl) {
            setAudioUrl(audioInfo.audioUrl);
          }

          // 更新记录信息
          setRecord(prev => ({
            ...prev,
            timestamp: new Date().toISOString(),
            duration: audioInfo.duration
          }));

          // 清除sessionStorage中的数据
          sessionStorage.removeItem('pendingAudio');

          // 自动开始转写流程 - 使用内联代码避免循环依赖
          setTimeout(async () => {
            try {
              setIsLoading(true);
              setError('');
              setRecord(prev => ({ ...prev, transcriptionStatus: 'processing' }));
              console.log('🎙️ 开始转写流程...');

              // 使用存储的base64音频数据，转换为Blob
              const base64Audio = audioInfo.audioBase64;
              const mimeType = audioInfo.mimeType || 'audio/wav';
              const byteCharacters = atob(base64Audio.split(',')[1]);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const audioBlob = new Blob([byteArray], { type: mimeType });

              console.log('📱 使用Qwen3 V2 API进行转写...');
              const transcribeResult = await apiService.transcribeV2(audioBlob, id);

              if (transcribeResult.success) {
                const transcriptionText = transcribeResult.text || transcribeResult.transcription || '';
                setRecord(prev => ({
                  ...prev,
                  transcriptionStatus: 'completed',
                  transcriptionText: transcriptionText
                }));
                console.log('✅ 转写完成:', transcriptionText);

                // 更新ref
                transcriptionTextRef.current = transcriptionText;

                // 保存转写结果到后端
                try {
                  const updateResponse = await apiService.updateRecord(id, {
                    transcriptionText: transcriptionText
                  });
                  if (updateResponse.success) {
                    console.log('✅ 转写结果已保存到后端');
                  }
                } catch (error) {
                  console.error('❌ 保存转写结果失败:', error);
                }

                // 转写完成后，音频文件已由转写服务保存，直接更新状态
                console.log('✅ 转写完成，音频文件已保存到后端');
                setRecord(prev => ({
                  ...prev,
                  hasAudioFile: true
                }));
                console.log('✅ 音频文件状态已更新: true');

                // 自动开始润色流程
                setTimeout(async () => {
                  try {
                    setRecord(prev => ({ ...prev, polishStatus: 'processing' }));
                    console.log('✨ 开始润色流程...');

                    const result = await apiService.polishText(
                      transcriptionText,
                      record.instructionText
                    );

                    if (result.success) {
                      setRecord(prev => ({
                        ...prev,
                        polishStatus: 'completed',
                        polishedText: result.polishedText
                      }));

                      // 更新ref
                      polishedTextRef.current = result.polishedText;
                      instructionRef.current = record.instructionText;

                      // 保存润色结果到后端
                      const response = await apiService.updateRecord(id, {
                        polishedText: result.polishedText,
                        polishInstruction: record.instructionText
                      });

                      if (response.success) {
                        console.log('✅ 润色结果已保存到后端');
                      }

                      console.log('✅ 润色完成:', result);
                    } else {
                      throw new Error(result.error || '润色失败');
                    }
                  } catch (error) {
                    console.error('润色失败:', error);
                    setRecord(prev => ({ ...prev, polishStatus: 'error' }));
                    setError('润色失败');
                  } finally {
                    setIsLoading(false);
                  }
                }, 1000);
              } else {
                throw new Error(transcribeResult.error || '转写失败');
              }
            } catch (error) {
              console.error('转写失败:', error);
              setRecord(prev => ({ ...prev, transcriptionStatus: 'error' }));
              setError(`转写失败: ${error.message || '未知错误'}`);
              setIsLoading(false);
            }
          }, 100);
        }
      } else {
        // 如果没有待处理数据，尝试从后端获取记录信息
        // 直接在这里实现加载逻辑，避免循环依赖
        try {
          console.log('📋 从后端加载记录:', id);
          const response = await apiService.getRecord(id);

          if (response.success) {
            const backendRecord = response.data.record || response.data;
            setDuration(backendRecord.durationSeconds || 0);

            // 更新记录状态
            setRecord(prev => ({
              ...prev,
              ...backendRecord,
              // 字段名转换：后端的fav转为前端的isFavorite
              isFavorite: backendRecord.fav || prev.isFavorite,
              timestamp: backendRecord.createdAt || new Date().toISOString(),
              // 更新指令文本 - 字段映射：后端的polishInstruction转为前端的instructionText
              instructionText: backendRecord.polishInstruction || prev.instructionText,
              // 设置数据库中的音频时长
              durationSeconds: backendRecord.durationSeconds || 0
            }));

            // 初始化refs用于比较变更
            const currentInstruction = backendRecord.polishInstruction || record.instructionText;
            polishedTextRef.current = backendRecord.polishedText || '';
            transcriptionTextRef.current = backendRecord.transcriptionText || '';
            instructionRef.current = currentInstruction;

  
            console.log('✅ 记录加载成功:', backendRecord);

            // 不自动下载音频文件，只在用户播放或下载时才获取
            // 只在首次加载时检查音频文件是否存在以更新UI状态
            if (!audioFileCheckedRef.current) {
              try {
                console.log('🎵 检查音频文件是否存在:', id);

                // 检查音频文件是否存在
                const existsResponse = await apiService.checkAudioFileExists(id);

                if (existsResponse.success) {
                  // 更新记录状态，表示有音频文件存在
                  setRecord(prev => ({
                    ...prev,
                    hasAudioFile: existsResponse.exists
                  }));

                  console.log('✅ 音频文件存在，但暂不下载，等待用户播放或下载操作');
                } else {
                  console.log('⚠️ 音频文件不存在');
                  setRecord(prev => ({
                    ...prev,
                    hasAudioFile: false
                  }));
                }
                audioFileCheckedRef.current = true;
              } catch (error) {
                console.error('❌ 检查音频文件失败:', error);
                setRecord(prev => ({
                  ...prev,
                  hasAudioFile: false
                }));
                audioFileCheckedRef.current = true;
              }
            }
          }
        } catch (error) {
          console.error('加载记录失败:', error);
          setError('加载记录失败');
        }
      }
    } catch (error) {
      console.error('初始化页面失败:', error);
      setError('页面初始化失败');
    }

    // 标记页面已初始化（存储当前的audioId）
    pageInitializedRef.current = id;
  }, [id]);

  // 加载音频文件用于播放（按需加载）
  const loadAudioFileForPlayback = useCallback(async (audioId, forceReload = false) => {
    try {
      // 如果已经有音频URL且不强制重新加载，则跳过下载
      if (audioUrl && !forceReload) {
        console.log('🎵 音频文件已缓存，跳过下载');
        return { success: true, audioUrl };
      }

      console.log('🎵 用户请求播放音频，开始按需下载:', audioId);

      // 检查音频文件是否存在
      const existsResponse = await apiService.checkAudioFileExists(audioId);

      if (existsResponse.success && existsResponse.exists) {
        console.log('🎵 音频文件存在，开始获取音频数据');

        // 获取音频文件
        const audioResponse = await apiService.getAudioFile(audioId);

        if (audioResponse.success) {
          // 设置音频URL用于播放
          setAudioUrl(audioResponse.audioUrl);

          // 更新记录状态，表示有音频文件
          setRecord(prev => ({
            ...prev,
            hasAudioFile: true
          }));

          console.log('✅ 音频文件按需加载完成，可以播放');
          return { success: true, audioUrl: audioResponse.audioUrl };
        } else {
          console.error('❌ 音频文件获取失败:', audioResponse);
          return { success: false, error: '音频文件获取失败' };
        }
      } else {
        console.log('⚠️ 音频文件不存在，无法播放');
        setRecord(prev => ({
          ...prev,
          hasAudioFile: false
        }));
        return { success: false, error: '音频文件不存在' };
      }
    } catch (error) {
      console.error('❌ 按需加载音频文件失败:', error);
      setRecord(prev => ({
        ...prev,
        hasAudioFile: false
      }));
      return { success: false, error: error.message };
    }
  }, [audioUrl]);

  // 操作按钮处理函数
  const handleRePolish = useCallback(async () => {
    if (!record.transcriptionText) {
      setError('没有可润色的文本');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      setRecord(prev => ({ ...prev, polishStatus: 'processing' }));
      console.log('✨ 开始润色流程...');

      const result = await apiService.polishText(
        record.transcriptionText,
        record.instructionText  // 使用当前用户输入的指令
      );

      if (result.success) {
        setRecord(prev => ({
          ...prev,
          polishStatus: 'completed',
          polishedText: result.polishedText
        }));

        // 更新ref
        polishedTextRef.current = result.polishedText;
        instructionRef.current = record.instructionText;

        // 保存润色结果到后端
        const response = await apiService.updateRecord(id, {
          polishedText: result.polishedText,
          polishInstruction: record.instructionText
        });

        if (response.success) {
          console.log('✅ 润色结果已保存到后端');
        }

        console.log('✅ 润色完成:', result);
      } else {
        throw new Error(result.error || '润色失败');
      }
    } catch (error) {
      console.error('润色失败:', error);
      setRecord(prev => ({ ...prev, polishStatus: 'error' }));
      setError('润色失败');
    } finally {
      setIsLoading(false);
    }
  }, [record.transcriptionText, record.instructionText, id]);

  const handleReWrite = useCallback(async () => {
    // 重新转写+润色 - 始终允许点击
    setIsLoading(true);
    setError('');

    try {
      setRecord(prev => ({ ...prev, transcriptionStatus: 'processing' }));
      console.log('🎙️ 开始重新转写流程...');

      let audioBlob;

      // 优先使用已有的音频数据
      if (audioData) {
        console.log('📱 使用已有的音频数据');
        const base64Audio = audioData.audioBase64;
        const mimeType = audioData.mimeType || 'audio/wav';
        const byteCharacters = atob(base64Audio.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        audioBlob = new Blob([byteArray], { type: mimeType });
      } else {
        // 如果没有本地音频数据，尝试从后端下载音频文件
        console.log('📥 没有本地音频数据，尝试从后端下载');
        try {
          const audioResult = await loadAudioFileForPlayback(id, true);
          if (audioResult.success && audioResult.audioUrl) {
            // 从URL获取音频数据
            const response = await fetch(audioResult.audioUrl);
            if (!response.ok) {
              throw new Error('音频文件下载失败');
            }
            audioBlob = await response.blob();
            console.log('✅ 音频文件下载成功');
          } else {
            throw new Error('音频文件不存在或无法访问');
          }
        } catch (downloadError) {
          console.error('❌ 音频文件下载失败:', downloadError);
          throw new Error('无法获取音频文件，请检查音频文件是否存在');
        }
      }

      // 开始转写
      console.log('📱 使用Qwen3 V2 API进行转写...');
      const transcribeResult = await apiService.transcribeV2(audioBlob, id);

      if (transcribeResult.success) {
        const transcriptionText = transcribeResult.text || transcribeResult.transcription || '';

        setRecord(prev => ({
          ...prev,
          transcriptionStatus: 'completed',
          transcriptionText: transcriptionText
        }));

        // 更新ref
        transcriptionTextRef.current = transcriptionText;

        console.log('✅ 重新转写完成:', transcriptionText);

        // 保存转写结果到后端
        try {
          const updateResponse = await apiService.updateRecord(id, {
            transcriptionText: transcriptionText
          });
          if (updateResponse.success) {
            console.log('✅ 重新转写结果已保存到后端');
          }
        } catch (error) {
          console.error('❌ 保存重新转写结果失败:', error);
        }

        // 重新转写完成后，音频文件已由转写服务保存，直接更新状态
        console.log('✅ 重新转写完成，音频文件已保存到后端');
        setRecord(prev => ({
          ...prev,
          hasAudioFile: true
        }));
        console.log('✅ 音频文件状态已更新: true');

        // 自动开始润色流程
        setTimeout(async () => {
          try {
            setRecord(prev => ({ ...prev, polishStatus: 'processing' }));
            console.log('✨ 开始润色流程...');

            const result = await apiService.polishText(
              transcriptionText,
              record.instructionText
            );

            if (result.success) {
              setRecord(prev => ({
                ...prev,
                polishStatus: 'completed',
                polishedText: result.polishedText
              }));

              // 更新ref
              polishedTextRef.current = result.polishedText;
              instructionRef.current = record.instructionText;

              // 保存润色结果到后端
              const response = await apiService.updateRecord(id, {
                polishedText: result.polishedText,
                polishInstruction: record.instructionText
              });

              if (response.success) {
                console.log('✅ 润色结果已保存到后端');
              }

              console.log('✅ 润色完成:', result);
            } else {
              throw new Error(result.error || '润色失败');
            }
          } catch (error) {
            console.error('润色失败:', error);
            setRecord(prev => ({ ...prev, polishStatus: 'error' }));
            setError('润色失败');
          } finally {
            setIsLoading(false);
          }
        }, 1000);
      } else {
        throw new Error(transcribeResult.error || '转写失败');
      }

    } catch (error) {
      console.error('重新转写失败:', error);
      setRecord(prev => ({ ...prev, transcriptionStatus: 'error' }));
      setError(`转写失败: ${error.message || '未知错误'}`);
      setIsLoading(false);
    }
  }, [audioData, id, record.instructionText, loadAudioFileForPlayback]);

  const handleDelete = useCallback(async () => {
    if (window.confirm('确定要删除这条记录吗？')) {
      try {
        setIsLoading(true);
        await apiService.deleteRecord(id);
        navigate('/records');
      } catch (error) {
        console.error('删除记录失败:', error);
        setError('删除记录失败');
        setIsLoading(false);
      }
    }
  }, [id, navigate]);

  const handleDownloadAudio = useCallback(async () => {
    try {
      console.log('📥 开始下载音频文件:', id);
      await apiService.downloadAudio(id);
    } catch (error) {
      console.error('下载音频失败:', error);
      setError('下载音频失败: ' + error.message);
    }
  }, [id]);

  const handleCopyPolishedText = useCallback(() => {
    if (!record.polishedText) {
      setError('没有可复制的润色文本');
      return;
    }

    // 使用统一的提示系统，不需要单独的函数

    try {
      navigator.clipboard.writeText(record.polishedText).then(() => {
        console.log('✅ 润色文本已复制到剪贴板');
        showSaveSuccessMessage('已复制到剪贴板');
      }).catch((error) => {
        console.error('复制失败:', error);
        setError('复制失败: ' + error.message);

        // 降级方案：创建临时输入框进行复制
        const textArea = document.createElement('textarea');
        textArea.value = record.polishedText;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('✅ 润色文本已复制到剪贴板（降级方案）');
        showSaveSuccessMessage('已复制到剪贴板');
      });
    } catch (error) {
      console.error('复制失败:', error);
      setError('复制失败: ' + error.message);
    }
  }, [record.polishedText]);

  const toggleFavorite = useCallback(async () => {
    const newFavoriteStatus = !record.isFavorite;

    // 先更新本地状态，让用户立即看到反馈
    setRecord({ ...record, isFavorite: newFavoriteStatus });

    // 然后保存到后端
    try {
      const response = await apiService.updateRecord(id, {
        fav: newFavoriteStatus
      });
      if (response.success) {
        console.log('✅ 收藏状态已保存到后端:', newFavoriteStatus);
      } else {
        // 如果保存失败，恢复原来的状态
        setRecord({ ...record, isFavorite: record.isFavorite });
        console.error('❌ 收藏状态保存失败');
      }
    } catch (error) {
      // 如果保存失败，恢复原来的状态
      setRecord({ ...record, isFavorite: record.isFavorite });
      console.error('❌ 收藏状态保存失败:', error);
    }
  }, [record, id]);

  // 自动调整textarea高度的函数
  const autoResizeTextarea = useCallback((textarea) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const showSaveSuccessMessage = useCallback((message) => {
    setSaveMessage(message);
    setShowSaveSuccess(true);
    setTimeout(() => {
      setShowSaveSuccess(false);
      setSaveMessage('');
    }, 1000);
  }, []);

  // 智能返回逻辑：根据来源页面决定返回路径
  const handleGoBack = useCallback(() => {
    const fromPage = location.state?.from;

    if (fromPage === 'record') {
      // 从录音页面来，返回录音页面
      navigate('/recording');
    } else if (fromPage === 'list') {
      // 从列表页面来，返回列表页面并回传状态信息
      navigate('/records', {
        state: {
          from: 'detail',
          clickedIndex: location.state?.clickedIndex,
          scrollPosition: location.state?.scrollPosition
        }
      });
    } else {
      // 默认返回录音页面
      navigate('/recording');
    }
  }, [navigate, location.state]);

  // 手动刷新音频文件状态
  const refreshAudioStatus = useCallback(async () => {
    try {
      console.log('🔄 手动刷新音频文件状态');
      const existsResponse = await apiService.checkAudioFileExists(id);
      if (existsResponse.success) {
        setRecord(prev => ({
          ...prev,
          hasAudioFile: existsResponse.exists
        }));
        console.log('✅ 音频文件状态已刷新:', existsResponse.exists);
        showSaveSuccessMessage(existsResponse.exists ? '音频文件已找到' : '音频文件不存在');
      }
    } catch (error) {
      console.error('❌ 刷新音频文件状态失败:', error);
      setError('刷新音频状态失败');
    }
  }, [id, showSaveSuccessMessage]);

  // 通用的字段更新处理函数
  const handleFieldUpdate = useCallback(async (fieldConfig) => {
    const {
      recordField,      // record中的字段名
      ref,              // 对应的ref
      apiField,         // API发送时的字段名
      successMessage    // 成功消息
    } = fieldConfig;

    if (record[recordField] !== ref.current) {
      try {
        const updateData = {};
        updateData[apiField] = record[recordField];

        const response = await apiService.updateRecord(id, updateData);
        if (response.success) {
          ref.current = record[recordField];
          showSaveSuccessMessage(successMessage);
        }
      } catch (error) {
        console.error(`保存${successMessage}失败:`, error);
      }
    }
  }, [id, record, showSaveSuccessMessage]);

  // 三个文本框的处理函数
  const handlePolishedTextBlur = () => handleFieldUpdate({
    recordField: 'polishedText',
    ref: polishedTextRef,
    apiField: 'polishedText',
    successMessage: '润色文本已保存'
  });

  const handleTranscriptionTextBlur = () => handleFieldUpdate({
    recordField: 'transcriptionText',
    ref: transcriptionTextRef,
    apiField: 'transcriptionText',
    successMessage: '转写文本已保存'
  });

  const handleInstructionBlur = () => handleFieldUpdate({
    recordField: 'instructionText',
    ref: instructionRef,
    apiField: 'polishInstruction',
    successMessage: '指令已保存'
  });

  const togglePlay = useCallback(async () => {
    if (!isPlaying) {
      // 准备播放：检查音频是否已加载
      let currentAudioUrl = audioUrl;

      if (!currentAudioUrl && record.hasAudioFile) {
        console.log('🎵 音频未加载，开始加载...');
        const result = await loadAudioFileForPlayback(id);

        // 直接使用加载结果中的URL，而不是依赖状态更新
        if (!result || !result.success) {
          console.error('❌ 无法播放：音频文件加载失败');
          setError('音频文件不可用，请刷新页面重试');
          return;
        }

        currentAudioUrl = result.audioUrl;
        console.log('🎵 使用加载结果中的URL:', currentAudioUrl ? '有URL' : '无URL');
      }

      // 检查音频URL是否可用（使用局部变量而不是状态）
      if (!currentAudioUrl) {
        console.error('❌ 无法播放：音频文件不可用');
        setError('音频文件不可用，请刷新页面重试');
        return;
      }

      // 先设置状态让audio元素渲染，然后等待元素准备好再播放
      setAudioUrl(currentAudioUrl);

      // 等待一小段时间让audio元素渲染完成
      setTimeout(() => {
        if (audioRef.current) {
          console.log('🎵 audio元素已准备好，开始播放');
          audioRef.current.play().catch(error => {
            console.error('音频播放失败:', error);
            setError('音频播放失败，请重试');
            setIsPlaying(false);
          });
        } else {
          console.error('❌ audio元素未找到');
          setError('音频播放失败，请刷新页面重试');
          setIsPlaying(false);
        }
      }, 100);

      setIsPlaying(true);
      return;
    }

    // 处理暂停逻辑
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    // 如果已经播放到这里，说明是播放开始的情况
    setIsPlaying(!isPlaying);
  }, [isPlaying, audioUrl, record.hasAudioFile, loadAudioFileForPlayback, id]);

  
  useEffect(() => {
    // 页面加载时初始化
    console.log('🚀 useEffect 触发页面初始化，audioId:', id);
    initializePage();
  }, [initializePage]);

  // 自动调整textarea高度的useEffect
  useEffect(() => {
    // 初始化时调整高度
    if (polishedTextareaRef.current) {
      autoResizeTextarea(polishedTextareaRef.current);
    }
    if (transcriptionTextareaRef.current) {
      autoResizeTextarea(transcriptionTextareaRef.current);
    }
    if (instructionTextareaRef.current) {
      autoResizeTextarea(instructionTextareaRef.current);
    }
  }, [autoResizeTextarea]);

  // 监听文本变化，自动调整高度
  useEffect(() => {
    if (polishedTextareaRef.current) {
      autoResizeTextarea(polishedTextareaRef.current);
    }
  }, [record.polishedText, autoResizeTextarea]);

  useEffect(() => {
    if (transcriptionTextareaRef.current) {
      autoResizeTextarea(transcriptionTextareaRef.current);
    }
  }, [record.transcriptionText, autoResizeTextarea]);

  useEffect(() => {
    if (instructionTextareaRef.current) {
      autoResizeTextarea(instructionTextareaRef.current);
    }
  }, [record.instructionText, autoResizeTextarea]);

  // 清理函数：组件卸载时释放音频URL
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        console.log('🧹 清理音频URL:', audioUrl);
      }
    };
  }, [audioUrl]);

  // 移除了会重置用户输入指令的 useEffect
  // 现在指令由用户完全控制，不会被后端数据覆盖

  const progressPercentage = (currentTime / duration) * 100;

  return (
    <div className="min-h-screen bg-dark-bg text-white pb-20">
      {/* 顶部导航栏 */}
      <div className="bg-dark-bg border-b border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          {/* 返回按钮 */}
          <button
            onClick={handleGoBack}
            className="text-white flex items-center space-x-2 flex-shrink-0"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span>返回</span>
          </button>

          {/* 中间内容区域 */}
          <div className="flex-1 mx-4 min-w-0 overflow-hidden">
            {/* 用户信息 */}
            {record.user && (
              <div className="flex flex-col mb-1">
                <span className="text-sm text-white font-medium truncate">
                  {record.user.name || '未知用户'}
                </span>
                <span className="text-xs text-gray-400 break-all">
                  用户ID: {record.user.id}
                </span>
              </div>
            )}
          </div>

          {/* 时间戳、时长和收藏按钮 */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <div className="text-right">
              {/* 记录ID显示 */}
              <div className="text-sm text-gray-300 whitespace-nowrap">
                记录ID: {record.audioId}
              </div>
              {/* 创建时间显示 */}
              <div className="text-xs text-gray-400 whitespace-nowrap">
                {new Date(record.timestamp).toLocaleString('zh-CN', {
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
            <button
              onClick={toggleFavorite}
              className="text-xl flex-shrink-0"
              disabled={isLoading}
            >
              {record.isFavorite ? '⭐' : '☆'}
            </button>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="bg-warning-red text-white px-4 py-3 text-center">
          {error}
        </div>
      )}

      {/* 保存成功提示 - 固定在页面顶部中央 */}
      {showSaveSuccess && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg animate-pulse">
            {saveMessage}
          </div>
        </div>
      )}

      {/* 音频播放器 */}
      <div className="px-4 py-4 border-b border-gray-800">
        <div className="flex items-center space-x-4">
          <button
            onClick={togglePlay}
            className="w-12 h-12 bg-primary-blue rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
            disabled={!record.hasAudioFile}
          >
            {isPlaying ? (
              <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent"></div>
            ) : (
              <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1"></div>
            )}
          </button>

          <div className="flex-1">
            <div className="flex items-center justify-between text-sm mb-1">
              <span>{formatTime(currentTime)}</span>
              <span className="flex items-center space-x-2">
                {/* 音频状态指示器 */}
                <span className={`text-xs ${record.hasAudioFile ? 'text-green-400' : 'text-red-400'}`}>
                  {record.hasAudioFile ? '●' : '○'}
                </span>
                {/* 动态时长 */}
                <span>{formatTime(duration)}</span>
                {/* 如果数据库时长不为0且与动态时长不同，显示数据库时长 */}
                {record.durationSeconds > 0 && Math.abs(record.durationSeconds - duration) > 1 && (
                  <span className="text-xs text-gray-400">
                    (DB: {formatTime(record.durationSeconds)})
                  </span>
                )}
              </span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-blue transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* 音频状态刷新按钮 */}
          <button
            onClick={refreshAudioStatus}
            className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
            title="刷新音频状态"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
          </button>
        </div>

        {/* 音频状态提示 */}
        {!record.hasAudioFile && (
          <div className="mt-2 text-xs text-yellow-400 text-center">
            音频文件暂时不可用，转写完成后将自动更新状态
          </div>
        )}
      </div>

      {/* 状态指示器 */}
      <div className="px-4 py-3 border-b border-gray-800">
        <div className="flex justify-center">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                record.transcriptionStatus === 'completed' ? 'bg-success-green' :
                record.transcriptionStatus === 'processing' ? 'bg-yellow-500 animate-pulse' :
                record.transcriptionStatus === 'error' ? 'bg-warning-red' : 'bg-gray-500'
              }`}></div>
              <span className="text-sm">
                {record.transcriptionStatus === 'completed' ? '转写完成' :
                 record.transcriptionStatus === 'processing' ? '转写中...' :
                 record.transcriptionStatus === 'error' ? '转写失败' : '等待转写'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                record.polishStatus === 'completed' ? 'bg-success-green' :
                record.polishStatus === 'processing' ? 'bg-yellow-500 animate-pulse' :
                record.polishStatus === 'error' ? 'bg-warning-red' : 'bg-gray-500'
              }`}></div>
              <span className="text-sm">
                {record.polishStatus === 'completed' ? '润色完成' :
                 record.polishStatus === 'processing' ? '润色中...' :
                 record.polishStatus === 'error' ? '润色失败' : '等待润色'}
              </span>
            </div>

            {isLoading && (
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-sm text-blue-500">处理中...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 修改指令 */}
      <div className="px-4 py-3 border-b border-gray-800">
        <label className="text-sm text-gray-300 mb-2 block">修改指令</label>
        <textarea
          ref={instructionTextareaRef}
          value={record.instructionText}
          onChange={(e) => {
            setRecord({ ...record, instructionText: e.target.value });
            autoResizeTextarea(e.target);
          }}
          onBlur={handleInstructionBlur}
          className="w-full bg-gray-900 rounded-lg p-3 text-white resize-y min-h-12 focus:ring-2 focus:ring-primary-blue focus:outline-none overflow-y-auto"
          placeholder="输入修改指令..."
        />
      </div>

      {/* 润色文本 */}
      <div className="px-4 py-3 border-b border-gray-800">
        <label className="text-sm text-gray-300 mb-2 block">润色文本</label>
        <div className="relative">
          <textarea
            ref={polishedTextareaRef}
            value={record.polishedText}
            onChange={(e) => {
              setRecord({ ...record, polishedText: e.target.value });
              autoResizeTextarea(e.target);
            }}
            onBlur={handlePolishedTextBlur}
            className="w-full bg-gray-900 rounded-lg p-3 text-white resize-y min-h-12 focus:ring-2 focus:ring-primary-blue focus:outline-none overflow-y-auto"
          />
        </div>
      </div>

      {/* 转写文本 */}
      <div className="px-4 py-3 border-b border-gray-800">
        <label className="text-sm text-gray-300 mb-2 block">转写文本</label>
        <textarea
          ref={transcriptionTextareaRef}
          value={record.transcriptionText}
          onChange={(e) => {
            setRecord({ ...record, transcriptionText: e.target.value });
            autoResizeTextarea(e.target);
          }}
          onBlur={handleTranscriptionTextBlur}
          className="w-full bg-gray-900 rounded-lg p-3 text-white resize-y min-h-12 focus:ring-2 focus:ring-primary-blue focus:outline-none overflow-y-auto"
        />
      </div>

      {/* 6个独立操作按钮 */}
      <div className="px-4 py-4 border-b border-gray-800">
        <div className="grid grid-cols-6 gap-2">
          <button
            onClick={handleRePolish}
            disabled={isLoading || !record.transcriptionText}
            className="flex flex-col items-center space-y-1 bg-gray-900 rounded-lg py-3 px-2 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-xl">{isLoading ? '⏳' : '🔄'}</span>
            <span className="text-xs text-gray-300">
              {isLoading ? '处理中' : '重新润色'}
            </span>
          </button>

          <button
            onClick={handleReWrite}
            disabled={isLoading}
            className="flex flex-col items-center space-y-1 bg-gray-900 rounded-lg py-3 px-2 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-xl">{isLoading ? '⏳' : '✏️'}</span>
            <span className="text-xs text-gray-300">
              {isLoading ? '处理中' : '重新转写并润色'}
            </span>
          </button>

          <button
            onClick={handleDownloadAudio}
            disabled={isLoading || !record.hasAudioFile}
            className="flex flex-col items-center space-y-1 bg-gray-900 rounded-lg py-3 px-2 hover:bg-green-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-xl">{record.hasAudioFile ? '📥' : '🚫'}</span>
            <span className="text-xs text-gray-300">
              {record.hasAudioFile ? '下载音频' : '音频不存在'}
            </span>
          </button>

          <button
            onClick={handleCopyPolishedText}
            disabled={isLoading || !record.polishedText}
            className="flex flex-col items-center space-y-1 bg-gray-900 rounded-lg py-3 px-2 hover:bg-blue-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-xl">📋</span>
            <span className="text-xs text-gray-300">复制</span>
          </button>

          <button
            onClick={handleDelete}
            disabled={isLoading}
            className="flex flex-col items-center space-y-1 bg-gray-900 rounded-lg py-3 px-2 hover:bg-red-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-xl">🗑️</span>
            <span className="text-xs text-gray-300">删除</span>
          </button>

          <button
            onClick={toggleFavorite}
            disabled={isLoading}
            className="flex flex-col items-center space-y-1 bg-gray-900 rounded-lg py-3 px-2 hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-xl">{record.isFavorite ? '⭐' : '☆'}</span>
            <span className="text-xs text-gray-300">收藏</span>
          </button>
        </div>
      </div>

      {/* 底部导航 */}
      <Navigation />

      {/* 隐藏的音频元素 */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
          onEnded={() => setIsPlaying(false)}
          onLoadedMetadata={(e) => {
            const audioDuration = e.target.duration || 0;
            // 只有当音频文件有有效时长且与当前时长差异较大时才更新
            // 这样可以保持数据库中的时长显示，避免显示0:00
            if (audioDuration > 0 && Math.abs(audioDuration - duration) > 1) {
              setDuration(audioDuration);
            }
          }}
        />
      )}
    </div>
  );
};

export default DetailPage;